import React, { useState, useEffect, useCallback } from "react";
import api, { extractApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, Search, Wallet } from "lucide-react";

// ---------------------------------------------------------------------------
// Iteration 94 — Admin > المالية : read-only Tap revenue reporting
// ---------------------------------------------------------------------------
const PRESETS = [
  ["today", "اليوم"], ["7d", "آخر 7 أيام"], ["month", "هذا الشهر"],
  ["year", "هذا العام"], ["all", "الكل"], ["custom", "فترة مخصصة"],
];
const KINDS = [
  ["all", "الكل"], ["session", "الجلسات"], ["book", "الكتاب"],
  ["upgrade", "ترقيات الجلسة"], ["refund", "الاستردادات"], ["tap", "Tap"], ["manual", "يدوي"],
];
const sar = (n) => `${Number(n || 0).toLocaleString("ar-SA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ر.س`;

const Card = ({ label, value, sub, testId, tone = "default" }) => (
  <div
    data-testid={testId}
    className={`rounded-2xl border p-4 ${tone === "muted" ? "bg-[#F6F4EE] border-[#E5E1D8]" : "bg-white border-[#E5E1D8]"}`}
  >
    <div className="text-xs text-[#5E5C58]">{label}</div>
    <div className={`mt-1 text-xl font-semibold ${tone === "muted" ? "text-[#5E5C58]" : "text-[#0A4D3C]"}`}>{value}</div>
    {sub && <div className="mt-1 text-[11px] text-[#8B8780]">{sub}</div>}
  </div>
);

const Pill = ({ active, onClick, children, testId }) => (
  <button
    type="button" data-testid={testId} onClick={onClick}
    className={`px-3 py-1 rounded-full text-xs border transition-colors ${
      active ? "bg-[#0A4D3C] text-white border-[#0A4D3C]" : "bg-white text-[#5E5C58] border-[#E5E1D8] hover:border-[#0A4D3C]"
    }`}
  >
    {children}
  </button>
);

const FinanceSection = () => {
  const [preset, setPreset] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [tx, setTx] = useState(null);
  const [quality, setQuality] = useState(null);
  const [loading, setLoading] = useState(false);

  const rangeParams = useCallback(() => {
    const p = { range: preset };
    if (preset === "custom") { if (from) p.date_from = from; if (to) p.date_to = to; }
    return p;
  }, [preset, from, to]);

  const load = useCallback(async () => {
    if (preset === "custom" && (!from || !to)) return;
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/admin/finance/summary", { params: rangeParams() }),
        api.get("/admin/finance/transactions", { params: { ...rangeParams(), kind, q: q || undefined, page, limit: 25 } }),
      ]);
      setSummary(s.data); setTx(t.data);
    } catch (e) {
      toast.error(extractApiErrorMessage(e) || "تعذّر تحميل البيانات المالية");
    } finally { setLoading(false); }
  }, [preset, from, to, kind, q, page, rangeParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/finance/data-quality").then((r) => setQuality(r.data)).catch(() => {});
  }, []);

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/finance/export.csv", {
        params: { ...rangeParams(), kind, q: q || undefined }, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `awtadk-finance-${preset}-${kind}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(extractApiErrorMessage(e) || "تعذّر التصدير"); }
  };

  const setPresetAndReset = (p) => { setPreset(p); setPage(1); };
  const setKindAndReset = (k) => { setKind(k); setPage(1); };
  const submitSearch = (e) => { e.preventDefault(); setQ(qInput.trim()); setPage(1); };

  const s = summary;
  return (
    <div data-testid="admin-finance" className="space-y-6" dir="rtl">
      <div className="card-warm p-5">
        <div className="flex items-center gap-2 mb-1">
          <Wallet className="w-5 h-5 text-[#0A4D3C]" />
          <h3 className="font-heading text-xl text-[#222222]">المالية — مبيعات Tap</h3>
        </div>
        <p className="text-sm text-[#5E5C58] leading-relaxed">
          تقرير قراءة فقط. تُحتسب الإيرادات من عمليات Tap الناجحة (المُلتقطة) فقط — إجمالي المبيعات قبل رسوم Tap،
          بما يقابل «Sale» في لوحة Tap. الحجوزات اليدوية تظهر للمعلومة فقط ولا تدخل ضمن الإيرادات.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="finance-range-filters">
          {PRESETS.map(([v, l]) => (
            <Pill key={v} active={preset === v} onClick={() => setPresetAndReset(v)} testId={`finance-range-${v}`}>{l}</Pill>
          ))}
          {preset === "custom" && (
            <div className="flex items-center gap-2 ms-2" data-testid="finance-custom-range">
              <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} data-testid="finance-date-from" className="h-8 w-40" />
              <span className="text-xs text-[#5E5C58]">إلى</span>
              <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} data-testid="finance-date-to" className="h-8 w-40" />
            </div>
          )}
        </div>
        {s?.range?.from && (
          <div className="mt-2 text-[11px] text-[#8B8780]" data-testid="finance-range-label">
            الفترة (توقيت الرياض): {s.range.from} → {s.range.to}
          </div>
        )}

        {s && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5" data-testid="finance-summary-cards">
              <Card label="إجمالي مبيعات Tap" value={sar(s.gross_tap_sales)} sub="قبل رسوم Tap (Sale)" testId="finance-gross" />
              <Card label="الاستردادات" value={sar(s.refunds)} sub={`${s.refunds_count} عملية استرداد`} testId="finance-refunds" />
              <Card label="صافي المبيعات بعد الاستردادات" value={sar(s.net_after_refunds)} testId="finance-net" />
              <Card label="عدد العمليات الناجحة" value={s.successful_count} sub="عمليات Tap فريدة" testId="finance-count" />
              <Card label="مبيعات الجلسات" value={sar(s.sessions_sales)} testId="finance-sessions" />
              <Card label="مبيعات الكتاب" value={sar(s.book_sales)} testId="finance-book" />
              <Card label="إيرادات الترقيات" value={sar(s.upgrade_revenue)} sub="فرق الترقية المدفوع فقط" testId="finance-upgrades" />
              <Card
                label="الحجوزات اليدوية" value={`${s.manual_bookings.count} حجز`}
                sub={`قيمة اسمية ${sar(s.manual_bookings.nominal_value)} — غير محتسبة ضمن الإيرادات`}
                testId="finance-manual" tone="muted"
              />
            </div>
            <div className="mt-3 rounded-xl bg-[#F6F4EE] border border-[#E5E1D8] p-3 text-xs text-[#5E5C58] flex flex-wrap gap-x-6 gap-y-1" data-testid="finance-discounts">
              <span>إجمالي الخصومات: <strong className="text-[#222222]">{sar(s.discounts.total)}</strong></span>
              <span>أكواد الخصم: {sar(s.discounts.coupon)}</span>
              <span>الخصومات التلقائية: {sar(s.discounts.auto)}</span>
              <span className="text-[#8B8780]">(من بيانات العمليات الناجحة المحفوظة فقط)</span>
            </div>
          </>
        )}
      </div>

      {s?.breakdown && (
        <div className="card-warm p-0 overflow-hidden" data-testid="finance-breakdown">
          <div className="px-5 pt-4 pb-2 font-heading text-lg text-[#222222]">الإيرادات حسب الخدمة / المنتج</div>
          <table className="w-full text-sm">
            <thead className="bg-[#F6F4EE] text-[#5E5C58]">
              <tr><th className="text-start p-3">الخدمة</th><th className="text-start p-3">الإيراد</th><th className="text-start p-3">عدد العمليات</th></tr>
            </thead>
            <tbody>
              {s.breakdown.length === 0 && <tr><td colSpan={3} className="p-4 text-xs text-[#8B8780]">لا توجد عمليات Tap ناجحة في هذه الفترة</td></tr>}
              {s.breakdown.map((b) => (
                <tr key={`${b.kind}-${b.service_id}`} className="border-t border-[#E5E1D8]" data-testid={`finance-breakdown-row-${b.service_id || "upgrade"}`}>
                  <td className="p-3">{b.title}</td>
                  <td className="p-3 text-[#0A4D3C] font-semibold">{sar(b.total)}</td>
                  <td className="p-3 text-xs text-[#5E5C58]">{b.count} عملية</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card-warm p-0 overflow-hidden" data-testid="finance-transactions">
        <div className="px-5 pt-4 pb-3 flex flex-wrap items-center gap-2 justify-between">
          <div className="flex flex-wrap gap-2" data-testid="finance-kind-filters">
            {KINDS.map(([v, l]) => (
              <Pill key={v} active={kind === v} onClick={() => setKindAndReset(v)} testId={`finance-kind-${v}`}>{l}</Pill>
            ))}
          </div>
          <form onSubmit={submitSearch} className="flex items-center gap-2">
            <Input
              value={qInput} onChange={(e) => setQInput(e.target.value)} data-testid="finance-search-input"
              placeholder="بحث: اسم، بريد، رقم فاتورة، مرجع Tap" className="h-8 w-64"
            />
            <Button type="submit" size="sm" variant="outline" className="rounded-full h-8" data-testid="finance-search-btn"><Search className="w-3.5 h-3.5" /></Button>
            <Button type="button" size="sm" onClick={exportCsv} className="rounded-full h-8 bg-[#0A4D3C] text-white" data-testid="finance-export-btn">
              <Download className="w-3.5 h-3.5 ms-1" /> تصدير CSV
            </Button>
          </form>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#F6F4EE] text-[#5E5C58]">
              <tr>
                {["التاريخ والوقت", "العميل", "نوع العملية", "الخدمة / المنتج", "السعر الأساسي", "الخصم", "المدفوع", "المسترد", "الصافي", "الوسيلة", "الحالة", "المرجع", "الفاتورة"].map((h) => (
                  <th key={h} className="text-start p-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tx?.items?.length === 0 && <tr><td colSpan={13} className="p-4 text-[#8B8780]">لا توجد عمليات مطابقة</td></tr>}
              {tx?.items?.map((r) => (
                <tr key={r.row_id} className={`border-t border-[#E5E1D8] ${r.counted ? "" : "bg-[#FBFAF6] text-[#8B8780]"}`} data-testid={`finance-row-${r.row_id}`}>
                  <td className="p-3 whitespace-nowrap" dir="ltr">{r.at_ksa}</td>
                  <td className="p-3">
                    <div className="text-[#222222]">{r.customer_name || "—"}</div>
                    <div className="text-[10px] text-[#8B8780]" dir="ltr">{r.customer_email}{r.client_number ? ` · #${r.client_number}` : ""}</div>
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    <Badge className={`rounded-full ${r.kind === "refund" ? "bg-rose-100 text-rose-800" : r.kind === "manual" ? "bg-gray-100 text-gray-700" : "bg-emerald-100 text-emerald-800"}`}>{r.kind_ar}</Badge>
                    {!r.counted && <div className="text-[10px] text-amber-700 mt-1">{r.note}</div>}
                  </td>
                  <td className="p-3">{r.service_title}</td>
                  <td className="p-3 whitespace-nowrap">{sar(r.base_amount)}</td>
                  <td className="p-3 whitespace-nowrap">{r.discount ? sar(r.discount) : "—"}</td>
                  <td className="p-3 whitespace-nowrap font-semibold text-[#0A4D3C]">{r.counted ? sar(r.paid_amount) : "—"}</td>
                  <td className="p-3 whitespace-nowrap">{r.refunded_amount ? sar(r.refunded_amount) : "—"}</td>
                  <td className="p-3 whitespace-nowrap">{r.counted ? sar(r.net) : "—"}</td>
                  <td className="p-3">{r.method_ar}</td>
                  <td className="p-3">{r.status_ar}</td>
                  <td className="p-3 text-[10px] break-all max-w-[140px]" dir="ltr">{r.reference}</td>
                  <td className="p-3 whitespace-nowrap">{r.invoice_number || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 flex items-center justify-between text-xs text-[#5E5C58]" data-testid="finance-pagination">
          <span>{tx ? `${tx.total} عملية — صفحة ${tx.page} من ${tx.pages}` : "..."}{loading ? " (تحميل...)" : ""}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full h-7" disabled={!tx || page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="finance-prev-page">السابق</Button>
            <Button size="sm" variant="outline" className="rounded-full h-7" disabled={!tx || page >= tx.pages} onClick={() => setPage((p) => p + 1)} data-testid="finance-next-page">التالي</Button>
          </div>
        </div>
      </div>

      {quality && (
        <div className="card-warm p-5" data-testid="finance-data-quality">
          <div className="font-heading text-lg text-[#222222] mb-2">جودة البيانات (للمراجعة فقط — لا يتم أي إصلاح تلقائي)</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs text-[#5E5C58]">
            <div>عمليات Tap ناجحة مرتبطة بحجز/شراء: <strong>{quality.tap_success_linked}</strong> / {quality.tap_success_total}</div>
            <div>عمليات Tap ناجحة بدون سجل مرتبط: <strong className={quality.tap_success_unlinked ? "text-rose-700" : ""}>{quality.tap_success_unlinked}</strong></div>
            <div>حجوزات مؤكدة (غير يدوية) بدون دفعة Tap ناجحة: <strong className={quality.paid_bookings_without_tap ? "text-amber-700" : ""}>{quality.paid_bookings_without_tap}</strong></div>
            <div>مشتريات مدفوعة بدون دفعة Tap ناجحة: <strong className={quality.paid_purchases_without_tap ? "text-amber-700" : ""}>{quality.paid_purchases_without_tap}</strong></div>
            <div>مراجع Tap مكرّرة: <strong className={quality.duplicate_tap_charge_groups ? "text-rose-700" : ""}>{quality.duplicate_tap_charge_groups}</strong> (صفوف زائدة: {quality.duplicate_tap_extra_rows})</div>
            <div>صفوف كوبون 100% (0 ر.س): {quality.coupon_bypass_rows}</div>
            <div className="sm:col-span-2 lg:col-span-3 pt-2 border-t border-[#E5E1D8]">
              الصيغة القديمة (كل payment_status=paid بما فيها اليدوي): <strong>{sar(quality.old_formula_total)}</strong>
              {" — "}صفوف يدوية قديمة: {quality.legacy_manual_paid_rows.count} بقيمة {sar(quality.legacy_manual_paid_rows.amount)}
              {" — "}الصيغة الجديدة (Tap فقط): <strong className="text-[#0A4D3C]">{sar(quality.new_formula_total)}</strong>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { FinanceSection };
